// Read-only "Invoice Summary" grid for the shared detail modal (app.md §8).
// Mirrors the Import Control editor's field set, labels, and layout
// (EditableInvoiceForm) so every step's modal reads the same — here the values
// are plain read-only text. AI notes are deliberately absent: they are Import
// Control's concern only (app.md §9 step 5).
import { Field, Input, makeStyles } from '@fluentui/react-components'
import type { Invoice } from '../lib/types'
import { formatCurrency, formatNumericDate } from '../lib/format'

const useStyles = makeStyles({
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' },
  // Fields that sit alone on their row span both columns (no orphan gap).
  fullRow: { gridColumn: '1 / -1' },
})

function ReadOnlyField({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <Field label={label} className={className}>
      <Input value={value} readOnly appearance="filled-darker" />
    </Field>
  )
}

export function InvoiceHeaderPanel({
  invoice,
  showPathToPointClickCare = true,
}: {
  invoice: Invoice
  /** The three company approval tiers — Department, Director, Oversight — do
      not show this row (owner item 13, 2026-08-12): they neither set the
      routing nor act on it, and since item 5 the control that sets it
      (Requires Manual Entry) is not in their action bar either. Verified safe
      for layout: this is the 11th of 11 fields and carries `s.fullRow`, so
      dropping it degrades the two-column grid to 10 fields in 5 even rows
      with no orphan gap. */
  showPathToPointClickCare?: boolean
}) {
  const s = useStyles()
  const text = (v: string | undefined | null) => (v == null || v === '' ? '—' : v)
  return (
    <div className={s.grid}>
      <ReadOnlyField label="Company" value={text(invoice.company_name)} />
      <ReadOnlyField label="Vendor" value={text(invoice.vendor_name)} />
      <ReadOnlyField label="Internal ID" value={text(invoice.id)} />
      <ReadOnlyField label="External ID" value={text(invoice.external_invoice_id)} />
      <ReadOnlyField label="Invoice Date" value={formatNumericDate(invoice.invoice_date)} />
      <ReadOnlyField label="Received Date" value={formatNumericDate(invoice.received_at)} />
      <ReadOnlyField label="Subtotal" value={formatCurrency(invoice.subtotal)} />
      <ReadOnlyField label="Tax" value={formatCurrency(invoice.tax)} />
      <ReadOnlyField label="Total" value={formatCurrency(invoice.total)} />
      <ReadOnlyField label="Currency" value={text(invoice.currency)} />
      {showPathToPointClickCare && (
        <ReadOnlyField label="Path to Point Click Care" value={invoice.manual_entry ? 'Manual Entry' : 'Export / Import'} className={s.fullRow} />
      )}
    </div>
  )
}
